import { isPlatformBrowser } from '@angular/common';
import { Component, computed, inject, OnInit, output, PLATFORM_ID, signal } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { map, Observable, switchMap, tap } from 'rxjs';
import { AuthService } from '../../../../core/services/auth/auth.service';
import { StorageKeys, StorageService } from '../../../../core/services/storage/storage.service';
import { ToastService } from '../../../../core/services/toast/toast.service';
import { CachedContent, Currency } from '../../../../interfaces/cached-content';
import { CurrencySymbolPipe } from '../../../../pipes/currency-symbol/currency-symbol.pipe';
import { RewardPointsService } from '../../../../services/reward-points/reward-points.service';
import { CacheService } from '../../../../ssr-services/cache/cache.service';
import { SubscriberService } from '../../../../ssr-services/subscriber/subscriber.service';

import { TranslateService } from '@ngx-translate/core';
import { GbTheme, THEME_SIGNAL } from '../../../../interfaces/theme';
@Component({
  selector: 'app-select-currency',
  imports: [],
  templateUrl: './select-currency.component.html',
  styleUrl: './select-currency.component.scss',
})
export class SelectCurrencyComponent implements OnInit {
  public platform = inject(PLATFORM_ID);

  public cacheService = inject(CacheService);

  public storageService = inject(StorageService);

  public theme = inject(THEME_SIGNAL);

  public GbTheme = GbTheme;

  public currencies = computed<Currency[]>(() => {
    const cacheContent = this.cacheService.cachedContent();
    if (!cacheContent) {
      return [];
    }

    return cacheContent.cache.config.currencyObj.currencyList;
  });

  public config = computed(() => (this.cacheService.cachedContent() as CachedContent).cache.config);

  public subscriberService = inject(SubscriberService);

  public rewardPointsService = inject(RewardPointsService);

  public authService = inject(AuthService);

  public selectedCurrencyCode = signal<string | null>(null);

  public currencySelected = output();

  public toastService = inject(ToastService);

  public currencySymbolPipe = new CurrencySymbolPipe();

  public domSanitizer = inject(DomSanitizer);

  public translateService = inject(TranslateService);

  public isLoggedIn = this.authService.isAuthenticated;

  public ngOnInit(): void {
    const subscriberCurrency = this.subscriberService.subscriberCurrency();
    this.selectedCurrencyCode.set(subscriberCurrency.currency);
  }

  /**
   * Selects the currency
   * @param data
   * @returns
   */
  selectCurrency(data: Pick<Currency, 'currency' | 'currency_name'>): Observable<boolean> {
    this.selectedCurrencyCode.set(data.currency);
    return this.subscriberService.updateCurrency(data).pipe(
      map(() => true),
      tap({
        next: () => {
          const newCurrency = this.subscriberService.subscriberCurrency().currency_name;
          const newCurrencyCode = this.subscriberService.subscriberCurrency().currency;
          const newCurrencySymbol = this.currencySymbolPipe.transform(newCurrencyCode);

          const rewardPointsEnabled = this.config().rewardPointsEnabled;
          if (this.isLoggedIn() && rewardPointsEnabled) {
            this.rewardPointsService.fetchUserRewardPointsData().subscribe();
          }

          if (isPlatformBrowser(this.platform) && !this.isLoggedIn()) {
            this.storageService.setKey(StorageKeys.SelectedCurrency, {
              currency: newCurrencyCode,
              currency_name: newCurrency,
            });
          }

          this.toastService
            .openSuccessToast(
              `${this.translateService.instant('currency.changed_successfully')} `,
              {
                specialContent: `${newCurrency} - ${newCurrencyCode} - ${newCurrencySymbol}`,
              }
            )
            .pipe(switchMap((toast) => toast.present()))
            .subscribe();

          this.currencySelected.emit();
        },
      })
    );
  }
}
